import { useState } from 'react';
import { adminMembers, AdminMember, MemberPlan, MemberStatus } from '@/mocks/admin';

const PLAN_STYLES: Record<MemberPlan, string> = {
  Starter: 'bg-white/8 text-white/60 border-white/15',
  Pro:     'bg-gold/10 text-gold border-gold/25',
  Elite:   'bg-amber-500/15 text-amber-300 border-amber-400/30',
};

const STATUS_STYLES: Record<MemberStatus, string> = {
  Active:    'bg-emerald-500/12 text-emerald-400 border-emerald-500/25',
  Inactive:  'bg-white/5 text-white/35 border-white/10',
  Pending:   'bg-blue-500/10 text-blue-400 border-blue-400/25',
  Suspended: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const STATUS_DOT: Record<MemberStatus, string> = {
  Active:    'bg-emerald-400',
  Inactive:  'bg-white/30',
  Pending:   'bg-blue-400',
  Suspended: 'bg-red-400',
};

type FilterStatus = 'All' | MemberStatus;
const FILTER_OPTIONS: FilterStatus[] = ['All', 'Active', 'Inactive', 'Pending', 'Suspended'];

export default function MembersTable() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('All');
  const [sortField, setSortField] = useState<keyof AdminMember>('joinDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 8;

  const handleSort = (field: keyof AdminMember) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = adminMembers
    .filter((m) => {
      const q = search.toLowerCase();
      const matchSearch = !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q) || m.state.toLowerCase().includes(q);
      const matchStatus = filterStatus === 'All' || m.status === filterStatus;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      const av = a[sortField] as string;
      const bv = b[sortField] as string;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === paginated.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map((m) => m.id)));
    }
  };

  const SortIcon = ({ field }: { field: keyof AdminMember }) => (
    <i className={`ml-1 text-xs ${
      sortField === field
        ? sortDir === 'asc' ? 'ri-arrow-up-line text-gold' : 'ri-arrow-down-line text-gold'
        : 'ri-arrow-up-down-line text-white/20'
    }`} />
  );

  return (
    <div className="bg-surface-2 border border-white/8 rounded-xl overflow-hidden">

      {/* ── Table header / controls ── */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 px-6 py-4 border-b border-white/8">
        <div>
          <h2 className="text-white font-semibold text-base">Members</h2>
          <p className="text-white/35 text-xs mt-0.5">{filtered.length} total records</p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:ml-auto w-full md:w-auto">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Search name, email, state…"
              className="w-full pl-9 pr-4 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg p-1 flex-shrink-0">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => { setFilterStatus(opt); setCurrentPage(1); }}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  filterStatus === opt
                    ? 'bg-gold text-navy-dark'
                    : 'text-white/40 hover:text-white/70'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bulk action bar ── */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 px-6 py-3 bg-gold/8 border-b border-gold/15">
          <span className="text-gold text-sm font-semibold">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 text-white/60 text-xs font-semibold rounded-lg hover:border-white/20 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-send-line" />
              Email Selected
            </button>
            <button className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold rounded-lg hover:bg-red-500/15 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-forbid-line" />
              Suspend
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="border-b border-white/5">
              <th className="w-10 px-6 py-3">
                <input
                  type="checkbox"
                  checked={selectedIds.size === paginated.length && paginated.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 accent-amber-500 cursor-pointer"
                />
              </th>
              {([
                { label: 'Member', field: 'name' as keyof AdminMember },
                { label: 'Plan', field: 'plan' as keyof AdminMember },
                { label: 'Status', field: 'status' as keyof AdminMember },
                { label: 'State', field: 'state' as keyof AdminMember },
                { label: 'Joined', field: 'joinDate' as keyof AdminMember },
                { label: 'Last Login', field: 'lastLogin' as keyof AdminMember },
                { label: 'Revenue', field: 'revenue' as keyof AdminMember },
              ]).map(({ label, field }) => (
                <th
                  key={field}
                  onClick={() => handleSort(field)}
                  className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-widest cursor-pointer hover:text-white/60 transition-colors whitespace-nowrap"
                >
                  {label}
                  <SortIcon field={field} />
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-semibold text-white/30 uppercase tracking-widest whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/4">
            {paginated.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-white/25 text-sm">
                  No members match your search
                </td>
              </tr>
            )}
            {paginated.map((member) => (
              <tr
                key={member.id}
                className={`group transition-colors hover:bg-white/3 ${selectedIds.has(member.id) ? 'bg-gold/4' : ''}`}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(member.id)}
                    onChange={() => toggleSelect(member.id)}
                    className="w-4 h-4 accent-amber-500 cursor-pointer"
                  />
                </td>

                {/* Name + email */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold/15 border border-gold/20 text-gold text-xs font-bold flex-shrink-0">
                      {member.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold whitespace-nowrap">{member.name}</p>
                      <p className="text-white/35 text-xs truncate max-w-[180px]">{member.email}</p>
                    </div>
                  </div>
                </td>

                {/* Plan */}
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-bold rounded-full border whitespace-nowrap ${PLAN_STYLES[member.plan]}`}>
                    {member.plan === 'Elite' && <i className="ri-vip-crown-line mr-1 text-xs" />}
                    {member.plan}
                  </span>
                </td>

                {/* Status */}
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full border whitespace-nowrap ${STATUS_STYLES[member.status]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOT[member.status]}`} />
                    {member.status}
                  </span>
                </td>

                {/* State */}
                <td className="px-4 py-4">
                  <span className="text-white/60 text-sm font-semibold">{member.state}</span>
                </td>

                {/* Joined */}
                <td className="px-4 py-4">
                  <span className="text-white/50 text-sm whitespace-nowrap">{member.joinDate}</span>
                </td>

                {/* Last login */}
                <td className="px-4 py-4">
                  <span className="text-white/50 text-sm whitespace-nowrap">{member.lastLogin}</span>
                </td>

                {/* Revenue */}
                <td className="px-4 py-4">
                  <span className="text-gold text-sm font-semibold whitespace-nowrap">{member.revenue}</span>
                </td>

                {/* Actions */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/8 transition-colors cursor-pointer">
                      <i className="ri-eye-line text-sm" />
                    </button>
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg text-white/40 hover:text-gold hover:bg-gold/8 transition-colors cursor-pointer">
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/8 transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-white/5">
        <p className="text-white/30 text-xs">
          Showing {Math.min((currentPage - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} members
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/8 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                currentPage === page
                  ? 'bg-gold text-navy-dark'
                  : 'text-white/40 hover:text-white hover:bg-white/8'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/8 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      </div>
    </div>
  );
}
